import { UserMemberBean } from "@gymflow/types";
import { useQuery } from "@tanstack/react-query";
import { useClubSettings } from "apps/portal/src/providers";
import { OnboardingContext } from "apps/portal/src/providers/OnboardingProvider/OnboardingContext";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { Button, EditIcon, Spinner } from "../../../atoms";
import { AppointmentsEmojiIcon } from "../../../atoms/icons/AppointmentsEmojiIcon";
import { ClassesEmojiIcon } from "../../../atoms/icons/ClassesEmojiIcon";
import { MarkerPin01Icon } from "../../../atoms/icons/MarkerPin01Icon";
import { VisitsEmojiIcon } from "../../../atoms/icons/VisitsEmojiIcon";
import { HostedPagesProfileCard } from "../../components/HostedPagesProfileCard";
import { EditMemberSidebarProviderContext } from "./HostedPagesEditProfile/EditMemberSidebarProviderContext";

type HostedPagesProfileUserBlockProps = {
  userMember?: UserMemberBean;
  isLoadingUserMember: boolean;
};

export const HostedPagesProfileUserBlock = ({
  userMember,
  isLoadingUserMember,
}: HostedPagesProfileUserBlockProps) => {
  const { openEditMemberSidebar } = useContext(
    EditMemberSidebarProviderContext,
  );
  const { currentScreen, setCurrentScreen } = useContext(OnboardingContext);

  const { club } = useClubSettings();
  const { t } = useTranslation();

  const { data: badges } = useQuery(
    apiV2.member.member.queries.profileBadges(userMember?.id),
  );

  const initials = (
    (userMember?.firstName[0] ?? "") + (userMember?.lastName[0] ?? "")
  ).toUpperCase();

  return (
    <HostedPagesProfileCard.Container className="flex-wrap">
      {isLoadingUserMember ? (
        <div className="flex h-16 w-full items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-3 overflow-hidden">
          <div className="flex w-full flex-row justify-between">
            <div className="flex items-center gap-3">
              <div className="flex size-16 shrink-0 justify-center overflow-hidden rounded-full border border-gray-300 dark:border-gray-700">
                {userMember?.picture ? (
                  <img
                    src={userMember?.picture}
                    alt="User's Profile"
                    className="flex aspect-square size-full object-cover"
                  />
                ) : (
                  <div className="dark:text-gray-25 flex items-center justify-center text-2xl font-semibold text-gray-950">
                    {initials}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-0.5">
                <div className="dark:text-gray-25 text-xl font-bold text-gray-950">{`${userMember?.firstName} ${userMember?.lastName}`}</div>
                <div className="flex items-center gap-1.5">
                  <MarkerPin01Icon
                    className="size-4"
                    pathClassName="stroke-gray-500 dark:stroke-gray-400"
                  />
                  <div className="text-base font-medium text-gray-500 dark:text-gray-400">{`${club.name}`}</div>
                </div>
              </div>
            </div>
            <Button
              intent="transparent"
              className="cursor-pointer p-2 opacity-50"
              onClick={() => {
                if (currentScreen === "banner") {
                  setCurrentScreen("form");
                } else {
                  openEditMemberSidebar();
                }
              }}
            >
              <EditIcon
                className="size-5"
                pathClassName="stroke-gray-500 stroke-2 dark:stroke-gray-400"
              />
            </Button>
          </div>
          {badges && (
            <div className="flex flex-row gap-1.5 overflow-x-auto">
              {badges.visitCount !== 0 && (
                <div className="flex flex-row items-center gap-0.5 rounded-3xl border border-gray-200 bg-gray-50 px-1.5 py-1 dark:border-gray-800 dark:bg-gray-900">
                  <VisitsEmojiIcon />
                  <div className="dark:text-gray-25 text-xs font-medium text-gray-950">
                    {t(
                      "pages.hostedPagesProfile.hostedPagesProfileUserBlock.badges.visit",
                      {
                        visitCount: badges.visitCount,
                      },
                    )}
                  </div>
                </div>
              )}
              {badges.classCount !== undefined && (
                <div className="flex flex-row gap-0.5 rounded-3xl border border-gray-200 bg-gray-50 px-1.5 py-1 dark:border-gray-800 dark:bg-gray-900">
                  <ClassesEmojiIcon />
                  <div className="dark:text-gray-25 text-xs font-medium text-gray-950">
                    {t(
                      "pages.hostedPagesProfile.hostedPagesProfileUserBlock.badges.class",
                      {
                        classCount: badges.classCount,
                      },
                    )}
                  </div>
                </div>
              )}
              {badges.appointmentCount !== undefined && (
                <div className="flex flex-row items-center gap-0.5 rounded-3xl border border-gray-200 bg-gray-50 px-1.5 py-1 dark:border-gray-800 dark:bg-gray-900">
                  <AppointmentsEmojiIcon />
                  <div className="dark:text-gray-25 text-xs font-medium text-gray-950">
                    {t(
                      "pages.hostedPagesProfile.hostedPagesProfileUserBlock.badges.appointment",
                      {
                        appointmentCount: badges.appointmentCount,
                      },
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </HostedPagesProfileCard.Container>
  );
};
